/**
 * Learning Rules Management Page
 *
 * Allows users to:
 * - View all learned rules for the selected mandate
 * - Filter rules by status (All, Active, Needs Review, Inactive, Shared)
 * - Search rules by pattern
 * - Create new rules manually
 * - Edit existing rules
 * - Toggle rule status
 * - Share rules with organization
 * - Copy rules between mandates
 * - View rule details and application history
 */

import { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/layout/page-header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RuleCard } from '@/components/learning-rules/RuleCard';
import { NewRuleDialog } from '@/components/learning-rules/NewRuleDialog';
import { EditRuleDialog } from '@/components/learning-rules/EditRuleDialog';
import { RuleDetailsPanel } from '@/components/learning-rules/RuleDetailsPanel';
import { LearningRulesService } from '@/services/learningRulesService';
import { 
  analyzeHistoricalData, 
  saveLearnedRules,
  enhanceWithAI,
  type PatternRule,
  type LearningAnalysis 
} from '@/services/historical-learning-service';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import type {
  LearnedRule,
  RuleStatistics,
  RuleFilter,
} from '@/types/learningRules';
import {
  Loader2,
  Plus,
  Search,
  RefreshCw,
  BarChart3,
  GitCompare,
  AlertCircle,
  Upload,
  Brain,
} from 'lucide-react';

const LearningRules = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [statistics, setStatistics] = useState<RuleStatistics | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'needs_review' | 'inactive' | 'shared'>('all');

  // Dialog states
  const [newRuleDialogOpen, setNewRuleDialogOpen] = useState(false);
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<LearnedRule | null>(null);

  // Historical learning states
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [learningAnalysis, setLearningAnalysis] = useState<LearningAnalysis | null>(null);
  const [selectedRules, setSelectedRules] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  // Load rules on mount and when filters change
  useEffect(() => {
    if (user) {
      loadRules();
      loadStatistics();
    }
  }, [user, activeTab]);

  const loadRules = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const filters: RuleFilter = {
        status: activeTab,
        search: searchQuery || undefined,
      };

      const loadedRules = await LearningRulesService.getRules(filters);
      setRules(loadedRules);
    } catch (error) {
      console.error('Error loading rules:', error);
      toast({
        title: 'Fehler',
        description: 'Regeln konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    if (!user) return;

    try {
      const stats = await LearningRulesService.getStatistics(user.id);
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadRules();
    await loadStatistics();
    setRefreshing(false);
    toast({
      title: 'Aktualisiert',
      description: 'Regeln wurden neu geladen.',
    });
  };

  const handleSearch = () => {
    loadRules();
  };

  const handleToggleStatus = async (rule: LearnedRule) => {
    try {
      await LearningRulesService.toggleRuleStatus(rule.id, !rule.is_active);
      toast({
        title: 'Erfolg',
        description: `Regel wurde ${!rule.is_active ? 'aktiviert' : 'deaktiviert'}.`,
      });
      loadRules();
      loadStatistics();
    } catch (error) {
      console.error('Error toggling rule status:', error);
      toast({
        title: 'Fehler',
        description: 'Status konnte nicht geändert werden.',
        variant: 'destructive',
      });
    }
  };

  const handleShareRule = async (rule: LearnedRule) => {
    try {
      await LearningRulesService.shareRule(rule.id);
      toast({
        title: 'Erfolg',
        description: 'Regel wurde mit der Organisation geteilt.',
      });
      loadRules();
    } catch (error) {
      console.error('Error sharing rule:', error);
      toast({
        title: 'Fehler',
        description: 'Regel konnte nicht geteilt werden.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyRule = (rule: LearnedRule) => {
    // TODO: Implement copy to another mandate functionality
    toast({
      title: 'Info',
      description: 'Regel kopieren Funktion wird später implementiert.',
    });
  };

  const handleEditRule = (rule: LearnedRule) => {
    setSelectedRule(rule);
    setEditRuleDialogOpen(true);
  };

  const handleViewDetails = (rule: LearnedRule) => {
    setSelectedRule(rule);
    setDetailsPanelOpen(true);
  };

  const handleNewRule = () => {
    setNewRuleDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    loadRules();
    loadStatistics();
  };

  // Filter rules by search query (client-side additional filtering)
  const filteredRules = rules.filter((rule) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      rule.pattern_key.toLowerCase().includes(query) ||
      rule.suggested_debit_account.toLowerCase().includes(query) ||
      rule.suggested_credit_account.toLowerCase().includes(query)
    );
  });

  if (!user || !profile) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title="Lernregeln"
        subtitle="Verwalten Sie gelernte Klassifikationsregeln für Ihr Mandat"
      />

      <main className="flex-1 p-page-padding lg:p-page-padding-lg space-y-6">
          {/* HEADER WITH ACTIONS */}
          <Card className="border-2 border-primary/20 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">Ihre persönlichen Lernregeln</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Diese Regeln werden automatisch auf Ihre Transaktionen angewendet
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHistoricalDialogOpen(true)}
                    className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:border-purple-300"
                  >
                    <Brain className="h-4 w-4 mr-2" />
                    Learn from History
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                  <Button variant="default" onClick={handleNewRule}>
                    <Plus className="h-4 w-4 mr-2" />
                    Neue Regel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* STATISTICS */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-muted-foreground">Total Regeln</div>
                  <div className="text-3xl font-bold mt-1">
                    {statistics.total_rules}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-muted-foreground">Aktiv</div>
                  <div className="text-3xl font-bold mt-1 text-green-600">
                    {statistics.active_rules}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-muted-foreground">Ø Confidence</div>
                  <div className="text-3xl font-bold mt-1">
                    {(statistics.avg_confidence * 100).toFixed(0)}%
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-muted-foreground">Erfolgsrate</div>
                  <div className="text-3xl font-bold mt-1 text-blue-600">
                    {(statistics.avg_success_rate * 100).toFixed(0)}%
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    Needs Review
                  </div>
                  <div className="text-3xl font-bold mt-1 text-orange-600">
                    {statistics.needs_review_count}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* TABS AND SEARCH */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  setActiveTab(
                    value as 'all' | 'active' | 'needs_review' | 'inactive' | 'shared'
                  )
                }
                className="flex-1"
              >
                <TabsList>
                  <TabsTrigger value="all">Alle Regeln</TabsTrigger>
                  <TabsTrigger value="active">Aktiv</TabsTrigger>
                  <TabsTrigger value="needs_review">
                    Needs Review
                    {statistics && statistics.needs_review_count > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {statistics.needs_review_count}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="inactive">Inaktiv</TabsTrigger>
                  <TabsTrigger value="shared">Geteilt</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {}}>
                  <GitCompare className="h-4 w-4 mr-2" />
                  Vergleich
                </Button>
                <Button variant="outline" size="sm" onClick={() => {}}>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </div>
            </div>

            {/* SEARCH */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Pattern, Konto..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                />
              </div>
              <Button onClick={handleSearch} variant="secondary">
                Suchen
              </Button>
            </div>
          </div>

          {/* RULES LIST */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : filteredRules.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    Keine Regeln gefunden für dieses Mandat.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleNewRule}
                    className="mt-4"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Erste Regel erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredRules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  mandateName={profile?.first_name ? `${profile.first_name} ${profile.last_name}` : user?.email}
                  onEdit={handleEditRule}
                  onToggleStatus={handleToggleStatus}
                  onShare={handleShareRule}
                  onCopy={handleCopyRule}
                  onViewDetails={handleViewDetails}
                />
              ))
            )}
          </div>
        </main>

        {/* Dialogs */}
      <NewRuleDialog
        open={newRuleDialogOpen}
        onOpenChange={setNewRuleDialogOpen}
        mandateId={user?.id || ''}
        mandateName={profile?.first_name ? `${profile.first_name} ${profile.last_name}` : user?.email || ''}
        onSuccess={handleDialogSuccess}
      />

      <EditRuleDialog
        open={editRuleDialogOpen}
        onOpenChange={setEditRuleDialogOpen}
        rule={selectedRule}
        mandateName={profile?.first_name ? `${profile.first_name} ${profile.last_name}` : user?.email}
        onSuccess={handleDialogSuccess}
      />

      <RuleDetailsPanel
        open={detailsPanelOpen}
        onOpenChange={setDetailsPanelOpen}
        rule={selectedRule}
        mandateName={profile?.first_name ? `${profile.first_name} ${profile.last_name}` : user?.email}
        onEdit={handleEditRule}
        onCopy={handleCopyRule}
        onShare={handleShareRule}
        onToggleStatus={handleToggleStatus}
      />

      {/* HISTORICAL LEARNING DIALOG */}
      <HistoricalLearningDialog
        open={historicalDialogOpen}
        onClose={() => {
          setHistoricalDialogOpen(false);
          setLearningAnalysis(null);
          setSelectedRules(new Set());
        }}
        onComplete={() => {
          loadRules();
          loadStatistics();
        }}
      />
    </AppLayout>
  );
};

// Historical Learning Dialog Component
const HistoricalLearningDialog = ({ 
  open, 
  onClose, 
  onComplete 
}: { 
  open: boolean; 
  onClose: () => void; 
  onComplete: () => void;
}) => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [learningAnalysis, setLearningAnalysis] = useState<LearningAnalysis | null>(null);
  const [selectedRules, setSelectedRules] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [originalRulesCount, setOriginalRulesCount] = useState(0);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAnalyzing(true);
    try {
      const analysis = await analyzeHistoricalData(file);
      setLearningAnalysis(analysis);
      setOriginalRulesCount(analysis.rulesGenerated.length);
      
      toast({
        title: 'Analysis Complete',
        description: `${analysis.patternsFound} patterns found in ${analysis.totalTransactions} transactions`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Analysis failed',
        variant: 'destructive',
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnhanceWithAI = async () => {
    if (!learningAnalysis) return;

    setEnhancing(true);
    const startTime = Date.now();
    
    try {
      const enhancedRules = await enhanceWithAI(learningAnalysis.rulesGenerated);
      const mergedCount = learningAnalysis.rulesGenerated.length - enhancedRules.length;
      
      setLearningAnalysis({
        ...learningAnalysis,
        rulesGenerated: enhancedRules,
        aiEnhanced: true,
        mergedPatterns: mergedCount,
        enhancementTimeMs: Date.now() - startTime,
      });

      toast({
        title: 'AI Enhancement Complete',
        description: `${enhancedRules.length} patterns (merged ${mergedCount} duplicates)`,
      });
    } catch (error: any) {
      toast({
        title: 'Enhancement Error',
        description: error.message || 'AI enhancement failed',
        variant: 'destructive',
      });
    } finally {
      setEnhancing(false);
    }
  };

  const handleSaveRules = async () => {
    if (!learningAnalysis || !user || !profile?.organization_id) return;

    const rulesToSave = learningAnalysis.rulesGenerated.filter((_, idx) => 
      selectedRules.has(idx)
    );

    if (rulesToSave.length === 0) {
      toast({
        title: 'No rules selected',
        description: 'Please select at least one rule',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const result = await saveLearnedRules(rulesToSave, profile.organization_id);
      
      toast({
        title: 'Rules Saved',
        description: `${result.success} rules created successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`,
      });

      onComplete();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save rules',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleRuleSelection = (idx: number) => {
    const newSelection = new Set(selectedRules);
    if (newSelection.has(idx)) {
      newSelection.delete(idx);
    } else {
      newSelection.add(idx);
    }
    setSelectedRules(newSelection);
  };

  const selectAllHighConfidence = () => {
    const highConfIndices = learningAnalysis?.rulesGenerated
      .map((rule, idx) => rule.confidence >= 85 ? idx : -1)
      .filter(idx => idx >= 0) || [];
    setSelectedRules(new Set(highConfIndices));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Learn from Historical Data</h2>
                <p className="text-sm text-gray-600">Upload transactions and generate rules automatically</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!learningAnalysis ? (
            // Upload Section
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <i className="fa-solid fa-clipboard-list"></i>
                  CSV Format Requirements
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  Your CSV should contain these columns (flexible names accepted):
                </p>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li><strong>Counterparty</strong> (or Partner, Gegenpartei)</li>
                  <li><strong>Description</strong> (or Beschreibung, Text)</li>
                  <li><strong>Account</strong> (or Konto, Account Number) - e.g., "6300" or "6300 - Office"</li>
                  <li><strong>Amount</strong> (optional - or Betrag, Value)</li>
                  <li><strong>VAT</strong> (optional - or MWST, VAT Rate)</li>
                </ul>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-purple-400 transition-colors">
                <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Upload Historical Transactions</h3>
                <p className="text-sm text-gray-600 mb-4">
                  CSV file with previously classified transactions
                </p>
                <label className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer transition-colors">
                  {analyzing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5" />
                      Select CSV File
                    </>
                  )}
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    disabled={analyzing}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          ) : (
            // Results Section
            <div className="space-y-6">
              {/* AI Enhancement Banner */}
              {!learningAnalysis.aiEnhanced && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Brain className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Enhance with AI (Optional)</h3>
                        <p className="text-sm text-gray-600">
                          Let Claude analyze and merge similar patterns for better accuracy
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleEnhanceWithAI}
                      disabled={enhancing}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {enhancing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Enhancing...
                        </>
                      ) : (
                        <>
                          <Brain className="h-4 w-4 mr-2" />
                          Enhance with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Statistics */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                  <div className="text-sm text-blue-700 mb-1">Transactions</div>
                  <div className="text-2xl font-bold text-blue-900">{learningAnalysis.totalTransactions}</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                  <div className="text-sm text-green-700 mb-1">
                    {learningAnalysis.aiEnhanced ? 'Enhanced Rules' : 'High Confidence'}
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {learningAnalysis.rulesGenerated.length}
                    {learningAnalysis.aiEnhanced && learningAnalysis.mergedPatterns && learningAnalysis.mergedPatterns > 0 && (
                      <span className="text-sm text-green-600 ml-2">
                        (-{learningAnalysis.mergedPatterns})
                      </span>
                    )}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg">
                  <div className="text-sm text-yellow-700 mb-1">Low Confidence</div>
                  <div className="text-2xl font-bold text-yellow-900">{learningAnalysis.lowConfidencePatterns.length}</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                  <div className="text-sm text-purple-700 mb-1">Processing Time</div>
                  <div className="text-2xl font-bold text-purple-900">
                    {learningAnalysis.processingTimeMs}ms
                    {learningAnalysis.aiEnhanced && learningAnalysis.enhancementTimeMs && (
                      <span className="text-sm text-purple-600 block">
                        +{learningAnalysis.enhancementTimeMs}ms AI
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* AI Enhancement Success Banner */}
              {learningAnalysis.aiEnhanced && learningAnalysis.mergedPatterns && learningAnalysis.mergedPatterns > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="fa-solid fa-check-circle text-green-600 text-xl"></i>
                  <div>
                    <h3 className="font-semibold text-green-900">AI Enhancement Applied</h3>
                    <p className="text-sm text-green-700">
                      Merged {learningAnalysis.mergedPatterns} duplicate patterns for improved accuracy
                    </p>
                  </div>
                </div>
              )}

              {/* Action Bar */}
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">
                  {selectedRules.size} of {learningAnalysis.rulesGenerated.length} rules selected
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllHighConfidence}
                  >
                    Select High Confidence (≥85%)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRules(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Rules Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-12 p-3"></th>
                      <th className="text-left p-3 text-sm font-semibold">Pattern</th>
                      <th className="text-left p-3 text-sm font-semibold">Type</th>
                      <th className="text-left p-3 text-sm font-semibold">Account</th>
                      <th className="text-left p-3 text-sm font-semibold">Confidence</th>
                      <th className="text-left p-3 text-sm font-semibold">Freq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learningAnalysis.rulesGenerated.map((rule, idx) => (
                      <tr 
                        key={idx}
                        className={`border-b hover:bg-gray-50 cursor-pointer ${selectedRules.has(idx) ? 'bg-purple-50' : ''}`}
                        onClick={() => toggleRuleSelection(idx)}
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedRules.has(idx)}
                            onChange={() => toggleRuleSelection(idx)}
                            className="h-4 w-4 rounded"
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{rule.pattern_value}</div>
                          <div className="text-xs text-gray-500">{rule.reasoning}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{rule.pattern_type}</Badge>
                        </td>
                        <td className="p-3 font-mono text-sm">{rule.account}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                              <div 
                                className={`h-2 rounded-full ${
                                  rule.confidence >= 85 ? 'bg-green-600' :
                                  rule.confidence >= 75 ? 'bg-yellow-600' :
                                  'bg-orange-600'
                                }`}
                                style={{ width: `${rule.confidence}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold">{rule.confidence}%</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{rule.frequency}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {learningAnalysis && (
          <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRules}
              disabled={saving || selectedRules.size === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  Save {selectedRules.size} Rules
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningRules;
